import { Routes, Route, Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useState, useEffect, lazy, Suspense } from 'react'
import Sidebar from './components/Sidebar'
import Login from './pages/Login'

// 🚀 Lazy-loaded Route-Komponenten für Code-Splitting
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Pages = lazy(() => import('./pages/Pages'))
const Posts = lazy(() => import('./pages/Posts'))
const Stories = lazy(() => import('./pages/Stories'))
const Reels = lazy(() => import('./pages/Reels'))
const ViralTracking = lazy(() => import('./pages/ViralTracking'))
const Analytics = lazy(() => import('./pages/Analytics'))
const UserManagement = lazy(() => import('./pages/UserManagement'))
const CacheStatus = lazy(() => import('./pages/CacheStatus'))
const Settings = lazy(() => import('./pages/Settings'))
const Exporte = lazy(() => import('./pages/Exporte'))

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [userName, setUserName] = useState<string>('')
  const [isAdmin, setIsAdmin] = useState(false)

  // Funktion zum Aktualisieren des Auth-Status nach erfolgreichem Login
  const updateAuthStatus = () => {
    const userData = localStorage.getItem('user')
    if (userData) {
      try {
        const user = JSON.parse(userData)
        if (user && user.is_active) {
          setIsAuthenticated(true)
          setUserName(user.full_name)
          setIsAdmin(user.role === 'admin')
          return
        }
      } catch (parseError) {
        console.error('Error parsing user data:', parseError)
        localStorage.removeItem('user')
      }
    }
    setIsAuthenticated(false)
    setUserName('')
    setIsAdmin(false)
  }

  useEffect(() => {
    // Prüfe ob User bereits eingeloggt ist (Cookie vorhanden)
    const checkAuth = async () => {
      try {
        // 🔐 NEUER SUPABASE-AUTH: Prüfe localStorage für Benutzer-Daten
        const userData = localStorage.getItem('user')
        
        if (userData) {
          try {
            const user = JSON.parse(userData)
            if (user && user.is_active) {
              setIsAuthenticated(true)
              setUserName(user.full_name)
              setIsAdmin(user.role === 'admin')
              setIsLoading(false)
              return
            }
          } catch (parseError) {
            console.error('Error parsing user data:', parseError)
            localStorage.removeItem('user')
          }
        }
        
        // Fallback: Prüfe ob gültige Cookies vorhanden sind (Legacy)
        const hasCookie = document.cookie.includes('PHPSESSID') || document.cookie.includes('PORTALPHPSESSID')
        
        if (hasCookie) {
          // Versuche Berechtigungen zu prüfen um sicherzustellen dass die Session noch gültig ist
          try {
            const { facebookApi } = await import('./lib/api')
            const hasPermission = await facebookApi.checkFacebookPermission()
            
            if (hasPermission) {
              setIsAuthenticated(true)
              // TODO: Benutzername aus Session wiederherstellen
              // Für jetzt: Verwende einen Standard-Namen
              setUserName('Benutzer')
            } else {
              // Keine Berechtigung - Cookies löschen
              document.cookie = 'PHPSESSID=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
              document.cookie = 'PORTALPHPSESSID=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
              setIsAuthenticated(false)
            }
          } catch (error) {
            console.error('Permission check failed:', error)
            // Bei Fehler: Cookies löschen und neu anmelden
            document.cookie = 'PHPSESSID=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
            document.cookie = 'PORTALPHPSESSID=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
            setIsAuthenticated(false)
          }
        } else {
          setIsAuthenticated(false)
        }
      } catch (error) {
        console.error('Auth check failed:', error)
        setIsAuthenticated(false)
      } finally {
        setIsLoading(false)
      }
    }

    checkAuth()
  }, [])

  // Loading Screen
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
          <p className="text-white/60">Lade...</p>
        </div>
      </div>
    )
  }

  // Login Page
  if (!isAuthenticated) {
    return <Login updateAuthStatus={updateAuthStatus} />
  }

  // Main App (geschützt)
  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Sidebar */}
      <Sidebar />
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="glass-card m-6 mb-0 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gradient">
                Facebook Creator Dashboard
              </h1>
              <p className="text-white/60 text-sm mt-1">
                Analytics & Insights für deine Facebook-Pages
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Status Indicator */}
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-sm text-white/80">Live</span>
              </div>
              
              {/* User Avatar & Menu */}
              <div className="relative group">
                <button className="flex items-center space-x-2 hover:bg-white/10 rounded-lg px-3 py-2 transition-colors">
                  <div className="w-10 h-10 bg-gradient-to-r from-facebook-500 to-accent-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-medium text-sm">
                      {userName ? userName.split(' ').map(n => n[0]).join('').toUpperCase() : 'FB'}
                    </span>
                  </div>
                  <div className="text-left">
                    <div className="text-white font-medium text-sm">
                      {userName || 'Benutzer'}
                    </div>
                    <div className="text-white/60 text-xs">Angemeldet</div>
                  </div>
                </button>
                
                {/* Dropdown Menu */}
                <div className="absolute right-0 top-full mt-2 w-48 bg-white/10 backdrop-blur-lg rounded-lg border border-white/20 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="p-2">
                    <button
                      onClick={() => {
                        // 🔐 Supabase-User: localStorage löschen
                        localStorage.removeItem('user')
                        localStorage.removeItem('isAdmin')
                        // Sitzungstoken verwerfen, sonst bliebe es gültig
                        localStorage.removeItem('sitzung_token')
                        // Cookie löschen (Legacy)
                        document.cookie = 'PHPSESSID=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
                        document.cookie = 'PORTALPHPSESSID=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
                        // Auth-Status zurücksetzen
                        setIsAuthenticated(false)
                        setUserName('')
                        setIsAdmin(false)
                      }}
                      className="w-full text-left px-3 py-2 text-white/80 hover:text-white hover:bg-white/10 rounded transition-colors flex items-center space-x-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      <span>Abmelden</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>
        
        {/* Page Content */}
        <main className="flex-1 overflow-auto p-6 pt-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Suspense fallback={
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400 mx-auto mb-3"></div>
                  <p className="text-white/50 text-sm">Lade Seite...</p>
                </div>
              </div>
            }>
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/pages" element={<Pages />} />
                <Route path="/posts" element={<Posts />} />
                <Route path="/stories" element={<Stories />} />
                <Route path="/reels" element={<Reels />} />
                <Route path="/viral" element={<ViralTracking />} />
                <Route path="/analytics" element={<Analytics />} />
                {/* 🔐 Admin-Only Routen */}
                {isAdmin && (
                  <>
                    <Route path="/exporte" element={<Exporte />} />
                    <Route path="/user-management" element={<UserManagement />} />
                    <Route path="/cache-status" element={<CacheStatus />} />
                    <Route path="/settings" element={<Settings />} />
                  </>
                )}
              </Routes>
            </Suspense>
          </motion.div>
        </main>
      </div>
    </div>
  )
}

export default App 